import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgTagTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { SvgIconComponent } from 'angular-svg-icon';
import { OpenOnValuePressDirective } from '../../../../directives/open-on-value-press.directive';
import { MobilePortraitService } from '../../../../services/mobile-portrait.service';
import { MobileSearchablePickerComponent } from './mobile-searchable-picker.component';

export interface DropdownOption {
  label: string;
  value: string;
  group?: string;
  isNew?: boolean;
}

type DropdownValue = string | string[] | null;

@Component({
  selector: 'app-dropdown',
  imports: [
    FormField,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgTagTemplateDirective,
    OpenOnValuePressDirective,
    SvgIconComponent,
    MobileSearchablePickerComponent,
  ],
  templateUrl: './dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Keeps a caller's static `id` attribute off the host element, which would
  // otherwise duplicate the `id` the template below puts on the real ng-select.
  host: { '[attr.id]': 'null' },
})
export class DropdownComponent {
  private readonly mobilePortrait = inject(MobilePortraitService);

  readonly field = input.required<FieldTree<DropdownValue>>();
  readonly id = input('');
  readonly label = input('');
  readonly hideLabel = input(false);
  readonly placeholder = input('');
  readonly options = input<readonly unknown[]>([]);
  readonly multiple = input(false);
  readonly tagSelection = input(false);
  readonly searchable = input(true);
  readonly clearable = input(true);
  // A typed name is emitted as `newValuePrefix` + name, not an id, so the caller must create it.
  readonly allowNew = input(false);
  readonly newValuePrefix = input('');
  readonly dropdownPosition = input<'auto' | 'top' | 'bottom'>('auto');
  readonly appendTo = input('');
  readonly containerClass = input('flex flex-col gap-1.5');
  readonly selectClass = input('');
  readonly testId = input('');

  // The picker is a fullscreen view with nothing focused in it, so `app-field-host` cannot read the
  // span of this interaction off focus events — it takes it from here.
  readonly presenceInteracting = output<boolean>();

  readonly isMobilePortrait = this.mobilePortrait.isMobilePortrait;
  readonly pickerOpen = signal(false);
  readonly ngSelect = viewChild(NgSelectComponent);

  protected readonly state = computed(() => this.field()());

  protected readonly parsedOptions = computed(() => {
    const options = parseOptions(this.options());
    const current = this.state().value();
    const prefix = this.newValuePrefix();
    if (
      this.allowNew() &&
      typeof current === 'string' &&
      current.startsWith(prefix) &&
      current !== prefix &&
      !options.some(o => o.value === current)
    ) {
      options.push({
        label: current.slice(prefix.length),
        value: current,
        isNew: true,
      });
    }
    return options;
  });

  protected readonly addNew = (term: string): DropdownOption => ({
    label: term.trim(),
    value: `${this.newValuePrefix()}${term.trim()}`,
    isNew: true,
  });

  protected readonly groupBy = computed(() =>
    this.parsedOptions().some(option => option.group) ? 'group' : ''
  );

  protected readonly labelText = computed(() =>
    this.state().required() ? `${this.label()} *` : this.label()
  );

  protected readonly effectivePlaceholder = computed(
    () => this.placeholder() || 'Select an option...'
  );

  protected readonly showError = computed(() => {
    const state = this.state();
    return state.touched() && state.errors().length > 0;
  });

  protected readonly errorMessage = computed(
    () => this.state().errors()[0]?.message ?? 'This is a required field'
  );

  constructor() {
    effect(() => {
      if (!this.isMobilePortrait() && this.pickerOpen()) this.onPickerCancel();
    });
  }

  protected readonly usesMobilePicker = computed(
    () => this.isMobilePortrait() && this.searchable() && !this.allowNew()
  );

  protected onNgSelectOpen(): void {
    if (this.usesMobilePicker()) {
      this.ngSelect()?.close();
      this.setPickerOpen(true);
    }
  }

  protected onPickerCommit(value: DropdownValue): void {
    const state = this.state();
    state.value.set(value);
    state.markAsDirty();
    state.markAsTouched();
    this.setPickerOpen(false);
  }

  protected onPickerCancel(): void {
    this.setPickerOpen(false);
  }

  private setPickerOpen(open: boolean): void {
    this.pickerOpen.set(open);
    this.presenceInteracting.emit(open);
  }
}

function parseOptions(raw: readonly unknown[]): DropdownOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((option): DropdownOption | null => {
      if (typeof option === 'string') {
        const value = option.trim();
        return value ? { label: value, value } : null;
      }
      if (
        typeof option === 'object' &&
        option !== null &&
        'label' in option &&
        'value' in option
      ) {
        const record = option as Record<string, unknown>;
        const label =
          typeof record['label'] === 'string' ? record['label'].trim() : '';
        const value =
          typeof record['value'] === 'string' ? record['value'].trim() : '';
        const group =
          typeof record['group'] === 'string' ? record['group'].trim() : '';
        if (!label || !value) return null;
        const parsed: DropdownOption = group
          ? { label, value, group }
          : { label, value };
        return record['isNew'] === true ? { ...parsed, isNew: true } : parsed;
      }
      return null;
    })
    .filter((opt): opt is DropdownOption => opt !== null);
}
